import React from "react";
import { Topic, P, H3, H4, UL, Callout, Table, VisualBox } from "@/pages/knowledge/Atoms";

export default function Basics({ PayoffCall, PayoffPut }) {
  return (
    <>
      <Topic id="option-trading" title="What is Option Trading?">
        <P>
          Option trading is a type of trading in the stock market where you don&rsquo;t
          buy or sell the actual shares — instead, you trade the <b>right</b> to
          buy or sell them at a certain price, within a certain time. It&rsquo;s
          like making a <b>future deal</b> — you pay a small amount today to lock
          in a price for later.
        </P>
        <P>
          In India, options are traded on stock exchanges like{" "}
          <b>NSE (National Stock Exchange)</b> and{" "}
          <b>BSE (Bombay Stock Exchange)</b>, mainly on popular indices (Nifty,
          Bank Nifty, Sensex) and large-cap stocks (Reliance, Infosys, TCS, etc.).
        </P>

        <H3>A simple example</H3>
        <P>
          Reliance shares are trading at ₹1,250 today (assume post-split adjusted).
          You think the price will rise to ₹1,310 next week. Instead of buying
          500 shares (which would cost ₹6,25,000), you buy a <b>Call Option</b>{" "}
          that gives you the right to buy Reliance at ₹1,250. You pay a small{" "}
          <b>premium</b> — say ₹15 per share (₹7,500 for 1 lot of 500).
        </P>
        <P>
          If Reliance rises to ₹1,310, your option becomes valuable. You can sell
          it for, say, ₹70 — making a profit. If Reliance doesn&rsquo;t rise, you
          lose only the premium (₹7,500). <b>Small investment, big trade
          control</b> — that&rsquo;s the power of options.
        </P>

        <H3>Two types of options</H3>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <VisualBox title="CALL OPTION (CE) — RIGHT TO BUY">
            <PayoffCall />
            <p className="text-sm text-[#475569] mt-2">
              Used when you think the price will go <b>up</b>.
            </p>
          </VisualBox>
          <VisualBox title="PUT OPTION (PE) — RIGHT TO SELL">
            <PayoffPut />
            <p className="text-sm text-[#475569] mt-2">
              Used when you think the price will go <b>down</b>.
            </p>
          </VisualBox>
        </div>

        <H3>Expiry &amp; strike price</H3>
        <UL>
          <li>
            <b>Strike price</b>: the price at which you agree to buy or sell the
            asset (e.g., Reliance ₹1,250).
          </li>
          <li>
            <b>Expiry date</b>: the last day the option is valid. As of Dec 2025
            in India, Nifty index options expire <b>weekly on Tuesdays</b> and
            <b> monthly on the last Tuesday</b>. Bank Nifty &amp; FinNifty
            weekly expiries have been discontinued — they trade monthly only.
            Stock options expire <b>monthly on the last Thursday</b>.
          </li>
        </UL>

        <H3>Why traders use options</H3>
        <UL>
          <li>
            <b>To earn from price movements</b> — trade with less capital but
            higher potential return.
          </li>
          <li>
            <b>To hedge risk</b> — protect a portfolio from market falls (e.g.,
            using Put options).
          </li>
          <li>
            <b>To generate regular income</b> — advanced traders sell options to
            earn premiums.
          </li>
        </UL>

        <H3>Risks in option trading</H3>
        <Callout kind="warn">
          <UL>
            <li>The entire premium can be lost if the market doesn&rsquo;t move as expected.</li>
            <li>Option sellers face higher risk — losses can be unlimited on sharp moves.</li>
            <li>Time decay (Theta) erodes option value as expiry approaches.</li>
          </UL>
        </Callout>

        <H3>Option trading in India — quick reference</H3>
        <Table
          headers={["Item", "Detail (Dec 2025)"]}
          rows={[
            ["Regulator", "SEBI (Securities and Exchange Board of India)"],
            ["Traded on", "NSE & BSE"],
            ["Settlement", "Cash-settled for indices; physical for stocks"],
            ["Nifty 50 lot size", "75"],
            ["Bank Nifty lot size", "30"],
            ["Sensex lot size", "20"],
            ["Reliance lot size", "500"],
            ["Nifty weekly expiry", "Every Tuesday"],
            ["Stock options expiry", "Last Thursday of the month"],
          ]}
        />

        <P>
          <b>Example:</b> If Nifty is at <b>26,000</b>, one lot (75 units) of a
          Nifty option controls ₹19,50,000 worth of position — but you might pay
          only ₹5,000–₹10,000 as premium.
        </P>

        <Table
          headers={["Term", "Meaning", "Example"]}
          rows={[
            ["Call Option", "Right to Buy", "Expect price to go up"],
            ["Put Option", "Right to Sell", "Expect price to go down"],
            ["Premium", "Price paid for option", "₹15 per share"],
            ["Strike Price", "Agreed buy/sell price", "₹1,250"],
            ["Expiry", "Last trading date", "Every Tuesday (Nifty)"],
          ]}
        />

        <Callout kind="success">
          <b>In summary:</b> Option trading is a powerful financial tool that
          helps Indian traders <b>speculate, hedge, or earn income</b> with
          limited capital. Knowledge and discipline are key to success.
        </Callout>
      </Topic>

      <Topic id="futures-options" title="What are Futures and Options (F&O)?">
        <P>
          Futures and Options (F&amp;O) are financial contracts that let you
          trade in the <b>future value</b> of stocks, indices, or commodities —
          without owning them today. They&rsquo;re part of the <b>Derivatives
          Market</b>. You&rsquo;re not buying the share itself — you&rsquo;re
          making a deal about <b>what its price will be later</b>.
        </P>

        <H3>F&amp;O in simple terms</H3>
        <UL>
          <li>
            <b>Futures</b> — an agreement to buy or sell an asset at a fixed
            price on a future date. Both buyer and seller are <b>obligated</b>{" "}
            to complete the deal — no backing out.
          </li>
          <li>
            <b>Options</b> — gives the <b>right, but not the obligation</b>, to
            buy or sell at a fixed price. You pay a small <b>premium</b> for
            this right.
          </li>
        </UL>

        <H3>Easy example — from the Indian market</H3>
        <P>
          Reliance Industries is trading at <b>₹1,250</b> today.
        </P>
        <H4>Scenario 1: Futures contract</H4>
        <P>
          You expect Reliance to rise to ₹1,310 next month, so you buy a Reliance
          Futures contract at ₹1,250.
        </P>
        <UL>
          <li>If price rises to ₹1,310 → profit ₹60/share (₹30,000 on 1 lot of 500).</li>
          <li>If it falls to ₹1,190 → loss ₹60/share.</li>
        </UL>
        <Callout kind="warn">
          In Futures, profit or loss is unlimited — both sides must fulfill the
          deal.
        </Callout>

        <H4>Scenario 2: Options contract</H4>
        <P>
          You buy a Reliance ₹1,250 Call by paying a premium of ₹15.
        </P>
        <UL>
          <li>If Reliance rises to ₹1,310 → your option becomes valuable.</li>
          <li>If it falls below ₹1,250 → you lose only ₹15 × 500 = ₹7,500.</li>
        </UL>
        <Callout kind="success">
          In Options, you have a right but not an obligation — risk is limited
          to the premium you pay.
        </Callout>

        <H3>Futures vs Options — at a glance</H3>
        <Table
          headers={["Feature", "Futures", "Options"]}
          rows={[
            ["Meaning", "Obligation to buy/sell on future date", "Right (not obligation) to buy/sell"],
            ["Upfront Cost", "Margin money", "Premium"],
            ["Risk", "High (unlimited loss possible)", "Limited (to the premium)"],
            ["Reward", "High (unlimited)", "Also high, limited by movement"],
            ["Best for", "Experienced traders & hedgers", "Beginners & strategy traders"],
          ]}
        />

        <H3>Expiry &amp; lot size (Dec 2025)</H3>
        <UL>
          <li>
            <b>Index F&amp;O expiry:</b> Nifty weekly on Tuesday; monthly on the
            last Tuesday. Bank Nifty, FinNifty &amp; Midcap Nifty are monthly only.
            Sensex weekly is Tuesday.
          </li>
          <li>
            <b>Stock F&amp;O expiry:</b> Monthly on the last Thursday.
          </li>
          <li>
            <b>Lot sizes:</b> Nifty <b>75</b>, Bank Nifty <b>30</b>, Sensex{" "}
            <b>20</b>, FinNifty <b>65</b>; stocks vary (Reliance 500, TCS 175,
            Infosys 400).
          </li>
        </UL>

        <H3>Example from the Indian market</H3>
        <P>
          Nifty is at <b>26,000</b>. You can:
        </P>
        <UL>
          <li>
            Buy a Nifty Futures contract @ 26,000 (lot 75). Rise to 26,200 →
            profit ₹15,000. Fall to 25,800 → loss ₹15,000.
          </li>
          <li>
            Or, buy a Nifty 26,000 Call @ ₹100 premium → cost ₹7,500. Rise to
            26,200 → option value ~₹200 → profit ~₹7,500. Stay below 26,000 → you
            lose only the premium.
          </li>
        </UL>

        <Callout kind="success">
          F&amp;O are powerful — but require <b>discipline, knowledge and risk
          management</b>. Used wisely, they enhance both returns and protection.
        </Callout>
      </Topic>
    </>
  );
}
